import * as amplitude from '@amplitude/analytics-browser'
import type {
  BrowserOptions,
  EnrichmentPlugin,
} from '@amplitude/analytics-types'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { AmplitudeUser } from '@/types/AmplitudeUser'
import accountStore from '@/stores/account.store'
import unitStore from '@/stores/unit.store'
import { ConsentAwareContract } from '@/services/contracts/consent-aware.contract'
import usercentricsService from '@/services/usercentrics/usercentrics.service'
import apiService from '@/services/api.service'
import { ConsentType } from '@/enums'
import languageLevelMapper from '@/services/amplitude/language-level-mapper.service'
import { EventPayload } from '@/services/amplitude/interfaces/event-payload.interface'
import EventPayloadFactory from '@/services/amplitude/event-payload.factory'
import UniversityEntranceQualificationMapper from '@/services/amplitude/university-entrance-qualification-mapper.service'
import amplitudeExperimentService from '@/services/amplitude/amplitude-experiment.service'

class AmplitudeService implements ConsentAwareContract {
  private consent: boolean = false
  private sessionReplayTracking: EnrichmentPlugin | null = null

  constructor() {
    this.setupEventListeners()
  }

  public activate(): void | Promise<void> {
    if (usercentricsService.hasUserInteractedWithUsercentrics()) {
      this.consent = true
      this.enableSessionReplay()
      this.initializeAmplitude()
    }
  }

  public async deactivate(): Promise<void> {
    if (usercentricsService.hasUserInteractedWithUsercentrics()) {
      this.consent = false
      await this.disableSessionReplay()
      this.initializeAmplitude()
    }
  }

  private enableSessionReplay(): void {
    if (!this.sessionReplayTracking) {
      this.sessionReplayTracking = sessionReplayPlugin({
        sampleRate: 0.3, // This is overwritten by the remote config in the Amplitude dashboard. We use 30% as a fallback here.
      })
      amplitude.add(this.sessionReplayTracking)
    }
  }

  private async disableSessionReplay(): Promise<void> {
    if (this.sessionReplayTracking?.name) {
      await amplitude.remove(this.sessionReplayTracking.name).promise
      this.sessionReplayTracking = null
    }
  }

  private setupEventListeners() {
    window.addEventListener('account-data-saved', ((event: CustomEvent) => {
      this.trackEvents(event.detail.changedData)
    }) as EventListener)
  }

  private saveConsentForAmplitude(): void {
    apiService.account
      .putConsents([
        {
          type: ConsentType.AMPLITUDE,
          hasOptedIn: this.consent,
        },
      ])
      .catch((error) => {
        console.error('Failed to update consents:', error)
      })
  }

  private async initializeAmplitude() {
    try {
      await this.waitForAccount(3000)
      const accountData = accountStore.state.account

      if (!accountData?.uuid) {
        console.warn('Amplitude init: No valid UUID found in account data')
        return
      }

      this.saveConsentForAmplitude()

      await amplitude.init(this.getAmplitudeApiKey(), this.browserOptions())
        .promise

      const user = this.getAmplitudeUserData()

      this.identifyUser(user)

      if (this.consent) {
        await amplitudeExperimentService.initialize()
      } else {
        amplitudeExperimentService.teardown()
      }
    } catch (e) {
      console.warn(
        'Amplitude init: account data not loaded or initialization failed',
        e,
      )
    }
  }

  private browserOptions() {
    const serverUrl = this.getServerURL()

    const config: BrowserOptions = {
      serverZone: 'EU',
      serverUrl,
      // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#autocapture
      autocapture: {
        attribution: false,
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
        elementInteractions: true,
      },
      // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#optional-tracking
      trackingOptions: {
        ipAddress: true,
        language: true,
        platform: true,
      },
      // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#disable-cookies
      identityStorage: 'localStorage',
    }

    return config
  }

  private getAmplitudeApiKey() {
    return import.meta.env.VITE_AMPLITUDE_API_KEY
  }

  private getServerURL() {
    const baseURL = new URL(import.meta.env.VITE_AMPLITUDE_PROXY_BASE_URL)
    baseURL.pathname = 'no-consent'
    return baseURL.toString()
  }

  private waitForAccount(timeoutMs = 3000, intervalMs = 100): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now()
      const interval = setInterval(() => {
        const uuid = accountStore.state.account?.uuid
        if (uuid) {
          clearInterval(interval)
          resolve()
        } else if (Date.now() - start > timeoutMs) {
          clearInterval(interval)
          reject(new Error('Timeout waiting for account data'))
        }
      }, intervalMs)
    })
  }

  private calculateAge(birthday: string): number | null {
    if (!birthday) return null

    const birthDate = new Date(birthday)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--
    }

    return age
  }

  private getAmplitudeUserData(): AmplitudeUser {
    const accountData = accountStore.state.account
    const activeApplication = unitStore.getActiveApplication()

    const germanLevel =
      accountData?.languageSkills?.filter((lang) => lang.code === 'de')[0]
        ?.level || null

    return {
      email: accountData?.email || '',
      firstName: accountData?.firstName || '',
      lastName: accountData?.lastName || '',
      createdAt: accountData?.createdAt || '',
      campus: activeApplication?.campus?.name || null,
      startDate: activeApplication?.intake?.date || null,
      course: activeApplication?.course?.name || null,
      ageAtApplication: accountData?.birthday
        ? this.calculateAge(accountData.birthday)
        : null,
      country: accountData?.shippingCountry || null,
      city: accountData?.shippingCity || null,
      isInternational:
        accountStore.isDachRegion() !== null
          ? !accountStore.isDachRegion()
          : null,
      hzb_type: UniversityEntranceQualificationMapper.map(
        accountData?.universityEntranceQualification,
        accountData?.universityEntranceQualificationBw,
      ),
      germanLevel: languageLevelMapper.mapLevel(germanLevel),
    } as AmplitudeUser
  }

  private identifyUser(user: AmplitudeUser) {
    amplitude.setUserId(accountStore.state.account.uuid)

    const identifyEvent = new amplitude.Identify()

    Object.entries(user).forEach(([key, value]) => {
      if (value !== null) {
        identifyEvent.set(key as keyof AmplitudeUser, value)
      }
    })

    identifyEvent.set('consentFunctional', this.consent)

    amplitude.identify(identifyEvent)
  }

  public trackEvent(eventPayload: EventPayload) {
    if (!eventPayload || !eventPayload.eventProperties) {
      console.warn('AmplitudeService.trackEvent: Invalid eventPayload provided')
      return
    }

    const properties = eventPayload.eventProperties
    const impersonationId = accountStore.state.account?.uuid

    if (impersonationId) {
      properties.impersonationId = impersonationId
    }

    amplitude.track(eventPayload.eventType, properties)
  }

  private trackEvents(changedData: Record<string, unknown>) {
    Object.entries(changedData).forEach(([key]) => {
      const payload = EventPayloadFactory.buildPayload(key, changedData)
      if (payload) {
        this.trackEvent(payload)
      }
    })
    this.identifyUser(this.getAmplitudeUserData())
  }
}

export default new AmplitudeService()
